import {
  addDefaultProps,
  addDisplayName,
  addProps,
  addRef,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount, branchIfPassesPredicate } from 'ad-hok-utils'
import { isError, isLoading } from 'shared/src/utils/dataLoading'
import { AddLoadingAndErrorIndicators } from 'shared/src/utils/loading'
import { createUseStyles } from 'react-jss'
import { FC } from 'react'
import { typedAs } from 'shared/src/utils/typedAs'
import lottie from 'lottie-web'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Layout } from '../components/Layout'
import { LOADING_Z_INDEX } from './zIndex'
import animationData from '../assets/loading-animation.json'

const useStyles = createUseStyles({
  layout: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: LOADING_Z_INDEX,
    backgroundColor: '#F9F6F4',
  },
  container: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

interface LoadingProps {
  fullscreen?: boolean
}

export const Loading: FC<LoadingProps> = flowMax(
  addDisplayName('Loading'),
  addDefaultProps({
    fullscreen: true,
  }),
  addRef('divRef', typedAs<HTMLDivElement | null>(null)),
  addEffectOnMount(({ divRef }) => () => {
    const animation = lottie.loadAnimation({
      container: divRef.current!,
      renderer: 'svg',
      loop: true,
      animationData,
    })

    animation.play()
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ divRef, fullscreen, classes }) =>
    fullscreen ? (
      <Layout
        mode="creator"
        className={classes.layout}
        containerClassName={classes.container}
      >
        <div ref={divRef} />
      </Layout>
    ) : (
      <div ref={divRef} />
    )
)

export const addCmsLoadingAndErrorIndicators: AddLoadingAndErrorIndicators = (
  propName
) =>
  flowMax(
    addTranslationHelpers,
    branchIfPassesPredicate(propName as any, isError, {
      returns: ({ [propName]: dataLoading, t }) => (
        <div>{t('error', { message: dataLoading.error.message })}</div>
      ),
    }),
    branchIfPassesPredicate(propName as any, isLoading, {
      returns: () => <Loading />,
    }),
    addProps(({ [propName]: dataLoading }) => ({
      [propName]: (dataLoading as any).data,
    }))
  )
